import { AddCircleOutlineOutlined, DeleteOutline } from "@mui/icons-material";
import {
  Box,
  Grid,
  IconButton,
  MenuItem,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
  ListItemIcon,
  CircularProgress,
  FormControl,
  Autocomplete,
} from "@mui/material";
import CustomTextField from "../../../components/common/Textfield";
import Select from "../../../components/common/Select";
import { VenueFormData } from "../../../types/venue";
import { useEffect, useState, useCallback, useRef } from "react";
import PhoneNumberField from "../../../components/common/PhoneNumberField";
import venue1 from "../../../assets/venue1.jpg";
import venue2 from "../../../assets/venue2.jpg";
import cocktail from "../../../assets/cocktail.jpg";
import banquet from "../../../assets/banquet.png";
import apiService from "../../../service/Api";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/Index";
import { MuiTelInput, MuiTelInputInfo } from "mui-tel-input";
import { showErrorToast } from "../../../components/common/ShowToast";
import { useLocation } from "react-router-dom";
import ContactSelector from "../components/ContactAutoComplete";

interface IContact {
  id: string | number;
  name?: string;
  first_name?: string;
  last_name?: string;
  email: string | null;
  phone: string | null;
  contact_type: "individual" | "company";
  company_branch?: {
    id?: string | number;
    name: string;
    branch_rep: string;
    email: string;
    phone_number: string;
  } | null;
}

interface ServiceProvider {
  name: string;
  description: string | null;
}

interface ProviderOption {
  value: string;
  label: string;
}

interface VenueSimilarityResponse {
  status: string;
  message: string;
  similar_venues: {
    id: number;
    name: string;
    address_one: string;
    name_similarity: number;
    address_similarity: number;
  }[];
}

const VenueDetails = ({
  formData,
  handleInputChange,
  handleLayoutChange,
  addLayout,
  setFormData,
}: {
  formData: VenueFormData;
  handleInputChange: (field: string, value: any) => void;
  handleLayoutChange: (index: number, field: string, value: any) => void;
  addLayout: () => void;
  setFormData: React.Dispatch<React.SetStateAction<VenueFormData>>;
}) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const layoutImages: Record<string, string> = {
    theater: venue1,
    classroom: venue2,
    banquet: banquet,
    cocktail: cocktail,
  };

  const showPowerFields = formData.doesVenueHavePower === "yes";
  const location = useLocation();
  const [contactLoading, setContactLoading] = useState(false);
  const [venueHelperText, setVenueHelperText] = useState(
    !formData.venueName && !formData.id ? "Venue Title is required" : ""
  );
  const [contactOptions, setContactOptions] = useState<IContact[]>([]);
  const [newContactData, setNewContactData] = useState<Partial<IContact>>({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    contact_type: "individual",
  });
  const [savingNewContact, setSavingNewContact] = useState(false);
  const [contactSearchTerm, setContactSearchTerm] = useState("");

  const [backupPowerOptions, setBackupPowerOptions] = useState<
    ProviderOption[]
  >([]);
  const [addBackupPowerOpen, setAddBackupPowerOpen] = useState(false);
  const [newBackupPower, setNewBackupPower] = useState("");
  const [loadingBackupPower, setLoadingBackupPower] = useState(false);
  const [addBackupPowerError, setAddBackupPowerError] = useState<string | null>(
    null
  );

  const [openAddContactDialog, setOpenAddContactDialog] = useState(false);

  const debounce = (func: (...args: any[]) => void, delay: number) => {
    let timeout: ReturnType<typeof setTimeout>;
    return (...args: any[]) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), delay);
    };
  };

  const checkVenueSimilarity = useCallback(
    async (name: string, addressOne: string) => {
      if (name && addressOne) {
        try {
          const response = await apiService.post<VenueSimilarityResponse>(
            "/api/venues/venue/check-venue-similarity/",
            {
              name: name,
              address_one: addressOne,
            }
          );
          if (
            response.data.similar_venues &&
            response.data.similar_venues.length > 0
          ) {
            response.data.similar_venues.forEach((venue) => {
              showErrorToast(
                `A similar venue, "${venue.name}" at "${venue.address_one}", already exists. Please verify the details.`
              );
              setVenueHelperText(
                `A similar venue, "${venue.name}" already exists. Please verify the details.`
              );
            });
          } else {
            setVenueHelperText("");
          }
        } catch (error) {
          console.error("Venue similarity check failed:", error);
          showErrorToast("Failed to check for similar venues.");
        }
      }
    },
    []
  );

  const debouncedCheck = useRef(debounce(checkVenueSimilarity, 1000)).current;

  const handleInputChangeWithCheck = (field: string, value: any) => {
    handleInputChange(field, value);

    const isEditMode = location.pathname.toLowerCase().includes("edit");

    if (!isEditMode && (field === "venueName" || field === "addressOne")) {
      const updatedFormData = { ...formData, [field]: value };
      debouncedCheck(updatedFormData.venueName, updatedFormData.addressOne);
      console.log("formData", formData);
    } else if (isEditMode && field === "venueName") {
      setVenueHelperText(!value ? "Venue Title is required" : "");
    }
  };

  const handleOpenAddContactDialog = () => {
    setOpenAddContactDialog(true);
    setNewContactData({
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      contact_type: "individual",
    });
  };

  const handleCloseAddContactDialog = () => {
    setOpenAddContactDialog(false);
  };

  const fetchContacts = async (search: string) => {
    setContactLoading(true);
    try {
      const contactResponse = await apiService.get<{ results: IContact[] }>(
        `/api/contacts/contact/?contact_type=individual&search=${search}`
      );
      setContactOptions(contactResponse.data.results || []);
    } catch (err: any) {
      console.error("Failed to fetch contacts:", err);
      toast.error("Failed to load contacts. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setContactLoading(false);
    }
  };

  const fetchBackupPowerOptions = async () => {
    setLoadingBackupPower(true);
    try {
      const response = await apiService.get<ServiceProvider[]>(
        "/api/configurations/power-backups/"
      );
      const options = response.data.map((provider) => ({
        value: provider.name,
        label: provider.name,
      }));
      setBackupPowerOptions(options);
    } catch (error) {
      console.error("Failed to fetch backup power options:", error);
      toast.error("Failed to load backup power options.");
    } finally {
      setLoadingBackupPower(false);
    }
  };

  const handleAddBackupPower = async () => {
    if (newBackupPower.trim() !== "") {
      setAddBackupPowerError("");
      try {
        await apiService.post("/api/configurations/power-backups/", {
          name: newBackupPower,
        });
        await fetchBackupPowerOptions();
        setNewBackupPower("");
        setAddBackupPowerOpen(false);
        toast.success("Backup power option added successfully!");
      } catch (error: any) {
        console.error("Failed to add new backup power:", error);
        setAddBackupPowerError(
          error.response?.data?.name || "An error occurred. Please try again."
        );
        toast.error(error.response?.data?.name[0] || "An error occurred.");
      }
    }
  };

  const handleSaveNewContact = async () => {
    setSavingNewContact(true);
    try {
      const res = await apiService.post<IContact>(
        "/api/contacts/contact/",
        newContactData
      );
      const savedContact = res.data;

      toast.success("Contact added successfully!", {
        position: "top-right",
        autoClose: 3000,
        theme: "light",
      });

      setContactOptions((prevOptions) => [...prevOptions, savedContact]);

      setFormData((prevFormData) => ({
        ...prevFormData,
        contact_id: Number(savedContact.id),
        contactPersonName: `${savedContact.first_name || ""} ${
          savedContact.last_name || ""
        }`.trim(),
        contactPersonEmail: savedContact.email || "",
        contactPersonPhone: savedContact.phone || "",
      }));

      handleCloseAddContactDialog();
    } catch (error: any) {
      console.error("Failed to save new contact:", error);
      let errorMessage = "Failed to add contact. Please try again.";
      if (error.response?.data) {
        errorMessage =
          Object.values(error.response.data).flat().join(" ") || errorMessage;
      }
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setSavingNewContact(false);
    }
  };

  const handleNewContactChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setNewContactData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePhoneInputChange = (value: string, info: MuiTelInputInfo) => {
    setNewContactData((prev) => ({
      ...prev,
      phone: value,
    }));
  };
  const handleContactChange = (
    event: React.SyntheticEvent,
    value: IContact | null
  ) => {
    setFormData((prev) => ({
      ...prev,
      contact_id: Number(value?.id) || null,
      contactPersonName: `${value?.first_name || ""} ${value?.last_name || ""}`,
      contactPersonEmail: value?.email || "",
      contactPersonPhone: value?.phone || "",
    }));
  };
  const removeLayout = (index: number) => {
    const updatedLayouts = [...formData.layouts];
    updatedLayouts.splice(index, 1);
    setFormData((prev) => ({ ...prev, layouts: updatedLayouts }));
  };

  useEffect(() => {
    if (formData.doesVenueHavePower === "no") {
      setFormData((prevData) => ({
        ...prevData,
        powerType: "",
        powerOutageRate: "",
        powerBackup: "",
        backupPowerCapacity: "",
        power_distance_from_livestream_desk: "",
        powerSocketType: "",
      }));
    }
  }, [formData.doesVenueHavePower, setFormData]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (contactSearchTerm.length >= 3) {
        fetchContacts(contactSearchTerm);
      } else {
        setContactOptions([]);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [contactSearchTerm]);

  useEffect(() => {
    fetchBackupPowerOptions();
  }, []);

  return (
    <>
      <Box sx={{ mt: 5 }}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <CustomTextField
              label="Venue Name"
              placeholder="Placeholder"
              required
              value={formData.venueName}
              onChange={(e) =>
                handleInputChangeWithCheck("venueName", e.target.value)
              }
              helperText={venueHelperText}
              FormHelperTextProps={{
                sx: {
                  color: "red",
                },
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth>
              <Select
                label="Venue Type"
                labelId="venue-type-label"
                value={formData.venueType}
                onChange={(e) =>
                  handleInputChangeWithCheck("venueType", e.target.value)
                }
              >
                <MenuItem value="church">Church</MenuItem>
                <MenuItem value="chapel">Chapel</MenuItem>
                <MenuItem value="hall">Hall</MenuItem>
                <MenuItem value="tent">Tent</MenuItem>
                <MenuItem value="Funeral Parlour">Funeral Parlour</MenuItem>
                <MenuItem value="Private Residence">Private Residence</MenuItem>
                <MenuItem value="Family Home">Family Home</MenuItem>
                <MenuItem value="crematorium">Crematorium</MenuItem>
                <MenuItem value="other">Other</MenuItem>
                <MenuItem value="blank one">Blank 1</MenuItem>
                <MenuItem value="blank two">Blank 2</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <CustomTextField
              label="Address One"
              placeholder="Placeholder"
              value={formData.addressOne}
              onChange={(e) =>
                handleInputChangeWithCheck("addressOne", e.target.value)
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <CustomTextField
              label="Address Two"
              placeholder="Placeholder"
              value={formData.addressTwo}
              onChange={(e) =>
                handleInputChangeWithCheck("addressTwo", e.target.value)
              }
            />
          </Grid>

          {/* Row 2 */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <CustomTextField
              label="Suburb"
              placeholder="Placeholder"
              value={formData.suburb}
              onChange={(e) =>
                handleInputChangeWithCheck("suburb", e.target.value)
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <CustomTextField
              fullWidth
              label="City"
              name="city"
              value={formData.city}
              onChange={(e) =>
                handleInputChangeWithCheck("city", e.target.value)
              }
              variant="outlined"
              select
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {reduxTheme.cities.map((city) => (
                <MenuItem key={city} value={city}>
                  {city}
                </MenuItem>
              ))}
            </CustomTextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <CustomTextField
              label="Venue Capacity"
              placeholder="e.g. 500"
              type="number"
              value={formData.capacity}
              onChange={(e) =>
                handleInputChangeWithCheck("capacity", e.target.value)
              }
              inputProps={{ min: 0 }}
            />
          </Grid>

          {/* Layout Section */}
          <Grid size={{ xs: 12 }}>
            <Box sx={{ mt: 2 }}>
              {formData.layouts.map((layout, index) => (
                <Grid container spacing={2} key={index} sx={{ mb: 2 }}>
                  {/* Layout Type */}
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Select
                      label="Layout Type"
                      value={layout.layoutType}
                      onChange={(e) =>
                        handleLayoutChange(index, "layoutType", e.target.value)
                      }
                      renderValue={(selected) =>
                        typeof selected === "string" && selected
                          ? selected.charAt(0).toUpperCase() + selected.slice(1)
                          : ""
                      }
                    >
                      {["theater", "classroom", "banquet", "cocktail"].map(
                        (type) => (
                          <MenuItem key={type} value={type}>
                            <ListItemIcon>
                              <Tooltip
                                title={
                                  <img
                                    src={layoutImages[type]}
                                    alt={type + " layout"}
                                    style={{
                                      width: 180,
                                      height: 120,
                                      borderRadius: 8,
                                      border: "2px solid #eee",
                                      background: "#fff",
                                    }}
                                  />
                                }
                                placement="right"
                                arrow
                                enterDelay={200}
                              >
                                <img
                                  src={layoutImages[type]}
                                  alt={type + " layout"}
                                  style={{
                                    width: 32,
                                    height: 24,
                                    borderRadius: 4,
                                    border: "1px solid #eee",
                                    background: "#fff",
                                  }}
                                />
                              </Tooltip>
                            </ListItemIcon>
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </MenuItem>
                        )
                      )}
                    </Select>
                  </Grid>

                  {/* Capacity */}
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <CustomTextField
                      label="Capacity"
                      placeholder="Placeholder"
                      value={layout.capacity}
                      onChange={(e) =>
                        handleLayoutChange(index, "capacity", e.target.value)
                      }
                    />
                  </Grid>

                  {/* Delete Button (only show if more than 1 layout) */}
                  {formData.layouts.length > 1 && (
                    <Grid
                      size={{ xs: 12, sm: 6, md: 1 }}
                      sx={{ display: "flex", alignItems: "center" }}
                    >
                      <IconButton
                        onClick={() => removeLayout(index)}
                        color="error"
                      >
                        <DeleteOutline />
                      </IconButton>
                    </Grid>
                  )}

                  {/* Add Button (only show on last item) */}
                  {index === formData.layouts.length - 1 && (
                    <Grid
                      size={{ xs: 12, sm: 6, md: 2 }}
                      sx={{ display: "flex", alignItems: "center" }}
                    >
                      <IconButton onClick={addLayout}>
                        <AddCircleOutlineOutlined sx={{ mr: 1 }} />
                        <Typography>Add Layout</Typography>
                      </IconButton>
                    </Grid>
                  )}
                </Grid>
              ))}
            </Box>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            {/* <Autocomplete
              fullWidth
              options={contactOptions}
              getOptionLabel={(option: IContact) =>
                `${option.first_name || ""} ${option.last_name || ""}`.trim()
              }
              value={
                contactOptions.find((c) => c.id === formData.contact_id) ||
                (formData.contact_id
                  ? {
                      id: formData.contact_id,
                      first_name: formData.contactPersonName || "",
                      last_name: formData.contactPersonSurname || "",
                      email: formData.contactPersonEmail || "",
                      phone: formData.contactPersonPhone || "",
                      contact_type: "individual",
                    }
                  : null)
              }
              onChange={handleContactChange}
              onInputChange={(event, newInputValue) => {
                setContactSearchTerm(newInputValue);
              }}
              loading={contactLoading}
              renderInput={(params) => (
                <CustomTextField
                  {...params}
                  label="Contact Person"
                  variant="outlined"
                  helperText="Type at least 3 characters to search contacts"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {contactLoading && (
                          <CircularProgress
                            size={20}
                            sx={{
                              color:
                                reduxTheme.primary_colors?.[400] || "#614438",
                            }}
                          />
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      "& fieldset": {
                        borderColor: reduxTheme.primary_colors?.[300] || "#ddd",
                      },
                      "&:hover fieldset": {
                        borderColor: reduxTheme.neutral_colors?.[400] || "#bbb",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor:
                          reduxTheme.primary_colors?.[800] || "#614438",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: reduxTheme.neutral_colors?.[400] || "#666",
                    },
                    "& .MuiInputBase-input": {
                      color: reduxTheme.neutral_colors?.[100] || "#333",
                    },
                  }}
                />
              )}
            /> */}
            <ContactSelector
              value={
                formData.contact_id
                  ? {
                      id: formData.contact_id,
                      first_name: formData.contactPersonName,
                      last_name: formData.contactPersonSurname,
                      email: formData.contactPersonEmail,
                      phone: formData.contactPersonPhone,
                      contact_type: "individual",
                    }
                  : null
              }
              onChange={(contact) => {
                setFormData((prev) => ({
                  ...prev,
                  contact_id: contact?.id ? Number(contact.id) : null,
                  contactPersonName: contact?.first_name ?? "",
                  contactPersonSurname: contact?.last_name ?? "",
                  contactPersonEmail: contact?.email ?? "",
                  contactPersonPhone: contact?.phone ?? "",
                }));
              }}
            />

            <Grid
              size={{ xs: 12, sm: 6, md: 1 }}
              sx={{ display: "flex", alignItems: "center" }}
            >
              <IconButton onClick={handleOpenAddContactDialog}>
                <AddCircleOutlineOutlined sx={{ mr: 1 }} />
                <Typography>Add Contact Person</Typography>
              </IconButton>
            </Grid>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <CustomTextField
              label="Contact Person Email"
              placeholder="Placeholder"
              disabled
              value={formData.contactPersonEmail}
              onChange={(e) =>
                handleInputChangeWithCheck("contactPersonEmail", e.target.value)
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <PhoneNumberField
              value={formData.contactPersonPhone}
              disabled
              onChange={(e) =>
                handleInputChangeWithCheck("contactPersonPhone", e)
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}></Grid>

          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            {" "}
            <FormControl fullWidth>
              <Select
                label="Does Venue Have Power"
                value={formData.doesVenueHavePower}
                onChange={(e) =>
                  handleInputChangeWithCheck(
                    "doesVenueHavePower",
                    e.target.value
                  )
                }
              >
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {showPowerFields && (
            <>
              <Grid size={1}></Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth>
                  <Select
                    label="Power Type"
                    name="powerType"
                    value={formData.powerType}
                    onChange={(e) =>
                      handleInputChangeWithCheck("powerType", e.target.value)
                    }
                  >
                    <MenuItem value="zesa">Zesa</MenuItem>
                    <MenuItem value="generator">Generator</MenuItem>
                    <MenuItem value="solar">Solar</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth>
                  <Select
                    label="Power Outage Rate"
                    name="powerOutageRate"
                    value={formData.powerOutageRate}
                    onChange={(e) =>
                      handleInputChangeWithCheck(
                        "powerOutageRate",
                        e.target.value
                      )
                    }
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth>
                  <Select
                    label="Power Backup"
                    name="powerBackup"
                    value={formData.powerBackup}
                    onChange={(e) =>
                      handleInputChangeWithCheck("powerBackup", e.target.value)
                    }
                  >
                    {backupPowerOptions.map((option, idx) => (
                      <MenuItem key={option.value + idx} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <IconButton onClick={() => setAddBackupPowerOpen(true)}>
                  <AddCircleOutlineOutlined sx={{ mr: 1 }} />
                  <Typography>Add Backup Power</Typography>
                </IconButton>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth>
                  <Select
                    label="Backup Power Capacity"
                    value={formData.backupPowerCapacity}
                    onChange={(e) =>
                      handleInputChangeWithCheck(
                        "backupPowerCapacity",
                        e.target.value
                      )
                    }
                  >
                    <MenuItem value="500VA">500 VA</MenuItem>
                    <MenuItem value="1000VA">1000 VA</MenuItem>
                    <MenuItem value="2000VA">2000 VA</MenuItem>
                    <MenuItem value="5kVA">5 kVA</MenuItem>
                    <MenuItem value="10kVA">10 kVA</MenuItem>
                    <MenuItem value="10kVA">+ 20 kVA</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth>
                  <Select
                    label="Power Distance From Livestream Desk"
                    name="power_distance_from_livestream_desk"
                    value={formData.power_distance_from_livestream_desk}
                    onChange={(e) =>
                      handleInputChangeWithCheck(
                        "power_distance_from_livestream_desk",
                        e.target.value
                      )
                    }
                  >
                    <MenuItem value="10">0-10m</MenuItem>
                    <MenuItem value="25">10-25m</MenuItem>
                    <MenuItem value="50">25-50m</MenuItem>
                    <MenuItem value="50">50m+</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth>
                  <Select
                    label="Power Socket Type"
                    name="powerSocketType"
                    value={formData.powerSocketType}
                    onChange={(e) =>
                      handleInputChangeWithCheck(
                        "powerSocketType",
                        e.target.value
                      )
                    }
                  >
                    <MenuItem value="square">Square</MenuItem>
                    <MenuItem value="circle">Circle</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}
        </Grid>
      </Box>

      {/* Add Contact Person Dialog */}
      <Dialog open={openAddContactDialog} onClose={handleCloseAddContactDialog}>
        <DialogTitle>Add New Contact</DialogTitle>
        <DialogContent>
          <CustomTextField
            autoFocus
            margin="dense"
            name="first_name"
            label="First Name"
            required
            helperText={
              !newContactData.first_name ? "First Name is required" : ""
            }
            FormHelperTextProps={{
              sx: {
                color: "red",
              },
            }}
            type="text"
            fullWidth
            variant="outlined"
            value={newContactData.first_name}
            onChange={handleNewContactChange}
            sx={{ mb: 2 }}
          />
          <CustomTextField
            margin="dense"
            name="last_name"
            label="Surname"
            type="text"
            fullWidth
            variant="outlined"
            value={newContactData.last_name}
            onChange={handleNewContactChange}
            sx={{ mb: 2 }}
          />
          <CustomTextField
            margin="dense"
            name="email"
            label="Email Address"
            placeholder="janedoe@example.com"
            InputLabelProps={{ shrink: true }}
            type="email"
            fullWidth
            variant="outlined"
            value={newContactData.email}
            onChange={handleNewContactChange}
            sx={{ mb: 2 }}
          />

          <MuiTelInput
            margin="dense"
            name="phone"
            label="Phone Number"
            fullWidth
            FormHelperTextProps={{
              sx: {
                color: "red",
              },
            }}
            variant="outlined"
            value={newContactData.phone || ""}
            defaultCountry="ZW"
            onChange={handlePhoneInputChange}
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                "& fieldset": {
                  borderColor: reduxTheme.primary_colors[300],
                },
                "&:hover fieldset": {
                  borderColor: "#888",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#614438",
                },
              },
              "& .MuiInputLabel-root": {
                color: "#666",
              },
              "& .MuiInputBase-input": {
                color: "#333",
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseAddContactDialog}
            sx={{ backgroundColor: reduxTheme.primary_colors[800] }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveNewContact}
            disabled={savingNewContact}
            sx={{
              backgroundColor: reduxTheme.primary_colors[400],
              color: reduxTheme.primary_colors[900],
            }}
          >
            {savingNewContact ? <CircularProgress size={24} /> : "Save Contact"}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Add Backup Power Dialog */}
      <Dialog
        open={addBackupPowerOpen}
        onClose={() => setAddBackupPowerOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add New Backup Power Option</DialogTitle>
        <DialogContent>
          <CustomTextField
            fullWidth
            label="Backup Power Option"
            value={newBackupPower}
            onChange={(e) => setNewBackupPower(e.target.value)}
            variant="outlined"
            margin="normal"
            required
            error={!!addBackupPowerError}
            helperText={addBackupPowerError}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddBackupPowerOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddBackupPower}
            variant="contained"
            disabled={!newBackupPower.trim()}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VenueDetails;
